import * as yup from 'yup'
import { noodlesAddressSchema, noodlesRemittanceInstructionsSchema } from './noodles-order.js'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesQuoteLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  itemNote = 'itemNote', // note for the item
  discount = 'discount', // line item discount
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const noodlesQuoteFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  customerNumber: yup.string().nullable().optional(),
  offerNumber: yup.string().nullable().optional(),
  quoteContactName: yup.string().nullable().optional(),
  quoteContactNumber: yup.string().nullable().optional(),
  deliveryMethod: yup.string().nullable().optional(),
  paymentMethod: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  validUntilDate: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  customer: yup.object().shape({
    billingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        billingAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    shippingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        shippingAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        itemNote: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        discount: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  shippingFee: yup.string().nullable().optional(),
  otherFees: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  isNet: yup.string().nullable().optional(),
  showBankDetails: yup.string().nullable().optional(),
  showPaymentLink: yup.string().nullable().optional(),
})

export type NoodlesQuoteFileData = yup.InferType<typeof noodlesQuoteFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesQuoteFileData>

export const noodlesQuoteFileMappings: RequiredFileData = {
  issuer: {
    companyName: '442f3ee5-42a2-4ace-86fc-2e8643ca77f4',
    address: {
      addressLine1: '23c41538-25c1-4f98-997d-4f81cfb0236c',
      addressLine2: 'da3af9b7-c746-4336-88a5-4b3c0502980e',
      postalCode: '6c7a1514-0ca2-4e38-a5e7-dfc7bba72c04',
      city: 'c193c45a-1659-46a2-bd25-b8ca86876935',
      country: 'e6ceccaf-8e52-4736-abc9-e18cd2c84f18',
    },
    firstName: '663c659d-ebe6-4dc1-b52e-9713a0b500fd',
    lastName: '038bd861-0df6-42cd-b88d-1a07b0f9d11c',
    phoneNumber: '93f21060-510c-4104-bac1-86e4a3708f4e',
    emailAddress: '769d853a-f339-40f3-a2fb-4db844d30af3',
  },
  orderNumber: '10ea1f2f-abce-4029-b214-9ee22f6bb595',
  customerNumber: '32a361bb-aec6-407d-b2e1-bf0985f31582',
  offerNumber: 'b8da0443-49a7-4f0a-94a4-2f410429c93a',
  quoteContactName: '99715bf3-4e96-4df9-a09e-835782090744',
  quoteContactNumber: '74b91671-c370-45b9-a995-3d306748667f',
  deliveryMethod: 'b494af1c-8081-4bfd-8969-8019f510da3b',
  paymentMethod: '633164b8-dbe5-4e48-8d6a-dc0c1f2f6da9',
  paymentTerms: '117b6781-6999-40c7-acb7-a913b441429d',
  freightTerms: '7253c6b6-cc8e-4d30-9ae1-6341a4d10f3e',
  dateIssued: '1d0fe9d3-1bba-4154-8868-e29422612c52',
  validUntilDate: '25e9ea65-6ac8-4db6-9aa2-52f79bade849',
  externalNote: 'cd8f50c5-0b43-4aeb-ac4d-aade82b5c3a2',
  customer: {
    billingDetails: {
      firstName: '4bb8545e-8646-4d6a-bcd2-10138b81a97f',
      lastName: '9bc0427e-d59a-4681-8c89-2127d8bdc551',
      companyName: '3cf39204-0042-4dae-982d-a506d3cf1d24',
      billingAddress: {
        addressLine1: '0e37d783-988d-4989-92d9-f8bcf7578882',
        addressLine2: '20cca758-1b13-4915-bd94-87186cad74f5',
        postalCode: '3c5d58e6-adf5-471a-a5a6-de96421bce6e',
        city: '74679720-3d07-48cf-bf2f-85910d8a149d',
        country: 'da403628-872c-4f42-94e6-3925d76792e1',
      },
      phoneNumber: 'b6a10677-37dc-4dd8-9930-0efd6c002126',
      emailAddress: 'e37dc3ee-d568-4e12-ad1a-7904ee5be480',
    },
    shippingDetails: {
      firstName: '67d8e19d-aa13-4697-a84c-ca3d50dee6cc',
      lastName: '57a9b846-6796-4311-b8ee-c67249a0308d',
      companyName: '9eb0868b-48e9-4c28-a1fc-af290e2c4b36',
      shippingAddress: {
        addressLine1: '12fa266e-70c4-4d29-a159-1c732f6d745b',
        addressLine2: '4c59a4c1-e0e6-4abf-bae0-26d5b3cd5b82',
        postalCode: 'c0249747-162f-4240-8326-62779a8cb128',
        city: 'ea73df75-6fab-4b7a-8530-e783d0f16b7f',
        country: '86cd7a5d-79a8-43da-a6d2-1acd87a3498e',
      },
      phoneNumber: '7d4873da-faf6-4203-818b-4eac8f804da3',
      emailAddress: '4def002a-a9be-45d5-a333-f8551c177ea8',
    },
  },
  items: [
    {
      itemNumber: null,
      itemName: '93b27982-4631-4360-baae-405e51113bf1',
      description: '641d46b3-3931-4256-ba88-1ddf2f96ab11',
      itemQty: 'ee6f8e0e-c92c-446a-af4c-f5101fede64c',
      itemNote: '23b8c393-e5bb-4f0f-9cb4-72dbf4324db3',
      discount: 'f7f51e3a-5fcd-4406-826a-1f0985fea6aa',
      unitPrice: 'b48fb0e4-719b-46f6-950f-c76e6e0700b3',
      lineTotal: null,
    },
  ],
  taxRate: '19c3d647-1d25-4488-9d53-8da03c66dbab',
  shippingFee: '5e7b44d7-8624-4636-b4ed-94835cb997ca',
  otherFees: '5b6c6538-7cb5-460a-8261-43b0b0650976',
  agreement: '3b609d33-b2f5-488d-9b30-ada8f5f5e0e1',
  instructions: '14e6c612-4c11-4443-82e4-28563a5a3ddd',

  remittanceInstructions: {
    bankName: 'ba3693ac-ac97-4d1d-8a63-0b3bd81bd45a',
    bankAddress: {
      addressLine1: '178dbf31-c853-4e08-afc6-53466b78c101',
      addressLine2: '7d5d3b84-be11-4c18-90a8-5fd1269e93ff',
      postalCode: 'd766d75b-f1b4-435d-8de2-882e0d0027f5',
      city: '397de55d-6614-40b1-9833-0998fdaf3066',
      country: '21ee2d08-b6dd-46f8-8050-623d9e900845',
    },
    kto: '86168c79-e697-47da-a66e-0054839c52d5',
    blz: '1fbbc63f-03fd-48d8-a944-ecbda808e4f3',
    iban: '4d952d94-a0ce-4d2e-bc89-2ccd5a4b89dd',
    swift: 'a2f05d96-7fcd-4df9-8e22-5c78a617b75f',
    firstName: '5bea7cf4-ddc6-4b75-919f-efdd624a198e',
    lastName: '2578574b-0b7b-49c1-9641-1186e1c01730',
    ustIdNr: 'c19cfb1a-7ff9-4701-be93-4b7ffa558764',
    beneficiaryName: '91019c8c-9c41-4547-af6b-fd836bd8e9ca',
    beneficiaryAddress: {
      addressLine1: '3e3216b0-e5fa-46bd-94e1-21793549f714',
      addressLine2: 'a8665157-5200-406b-963f-b00106446351',
      postalCode: '00d0994e-29cf-4764-a02a-d80741c8bb7a',
      city: '4179f24a-bb97-4a25-a0b3-d16f204e329a',
      country: '8672a500-ae6b-44d8-a420-121b9d088d5b',
    },
    telephone: '1560a525-7450-43f1-81d4-e51de355ea35',
    telefax: '870d9270-d646-462b-b68c-3a30c6de3797',
    email: '0e1ca8e0-4274-43bc-880e-7b75fa7421ad',
    website: 'bada49ad-1be3-428f-923c-046d3dcece8c',
    paymentLink: '685c9ace-0fec-43dd-b41a-df1e6e12d268',
  },
  isNet: '98085d08-b85f-48b1-ba7e-8f8110491d3a',
  showBankDetails: '0007061e-5fdb-40a1-96d9-439149f3d35a',
  showPaymentLink: '7f4c8a46-c7e9-4460-88cd-fceafcdbcf3d',
}
